import React, { useState } from 'react';
import { testimonials } from '../data/mockProducts';
import { Card, CardContent } from './ui/card';
import { Star, Quote, ChevronLeft, ChevronRight, User } from 'lucide-react';

// Avatar placeholder component - grey silhouette
const AvatarPlaceholder = ({ gender, className }) => {
  const isMale = gender === 'male';
  return (
    <div className={`bg-gradient-to-br from-gray-300 to-gray-400 rounded-full flex items-center justify-center ${className}`}>
      <User className="w-12 h-12 text-gray-500" strokeWidth={1.5} />
    </div>
  );
};

const TestimonialsSection = () => {
  const [currentPage, setCurrentPage] = useState(0);
  const itemsPerPage = 8;
  const totalPages = Math.ceil(testimonials.length / itemsPerPage);
  
  const getCurrentPageItems = () => {
    const start = currentPage * itemsPerPage;
    return testimonials.slice(start, start + itemsPerPage);
  };

  const nextPage = () => {
    setCurrentPage((prev) => (prev + 1) % totalPages);
  };

  const prevPage = () => {
    setCurrentPage((prev) => (prev - 1 + totalPages) % totalPages);
  };

  // Determine gender based on name prefix
  const getGender = (name) => {
    if (name.startsWith('Puan') || name.startsWith('Cik')) return 'female';
    return 'male';
  };

  return (
    <section id="testimoni" className="py-20 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-800 px-4 py-2 rounded-full text-sm font-medium mb-4">
            <Star className="w-4 h-4 fill-amber-500 text-amber-500" />
            <span>Dipercayai Ribuan Pelanggan</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Apa Kata Pelanggan Kami
          </h2>
          <p className="text-base text-gray-600 max-w-2xl mx-auto">
            Setiap testimoni ada <span className="font-semibold text-amber-700">cerita istimewa</span> – dari yang datang jauh, beli untuk hadiah ibu, hingga lengkapkan rumah impian.
          </p>
        </div>

        {/* Testimonials Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 mb-10">
          {getCurrentPageItems().map((testimonial) => (
            <Card 
              key={testimonial.id} 
              className="group bg-white border border-gray-100 hover:border-amber-200 hover:shadow-lg transition-all duration-300"
            >
              <CardContent className="p-5">
                {/* Quote Icon */}
                <Quote className="w-8 h-8 text-amber-200 mb-3" />
                
                {/* Review Text */}
                <p className="text-gray-700 text-sm leading-relaxed mb-4 min-h-[60px]">
                  "{testimonial.text}"
                </p>
                
                {/* Rating */}
                <div className="flex gap-0.5 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} size={14} className="fill-amber-400 text-amber-400" />
                  ))}
                </div>
                
                {/* User Info */}
                <div className="flex items-center gap-3 pt-4 border-t border-gray-100">
                  <AvatarPlaceholder 
                    gender={getGender(testimonial.name)} 
                    className="w-10 h-10 flex-shrink-0"
                  />
                  <div>
                    <h4 className="font-semibold text-gray-900 text-sm">
                      {testimonial.name}
                    </h4>
                    <p className="text-xs text-gray-500">
                      {testimonial.location} • {testimonial.age}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-center gap-4">
          <button
            onClick={prevPage}
            className="p-2 rounded-full bg-white border border-gray-200 hover:bg-amber-50 hover:border-amber-300 transition-colors"
            aria-label="Previous"
          >
            <ChevronLeft className="w-5 h-5 text-gray-600" />
          </button>
          
          <div className="flex items-center gap-2">
            {[...Array(totalPages)].map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentPage(index)}
                className={`w-2.5 h-2.5 rounded-full transition-all ${
                  currentPage === index 
                    ? 'bg-amber-500 w-8' 
                    : 'bg-gray-300 hover:bg-gray-400'
                }`}
                aria-label={`Page ${index + 1}`}
              />
            ))}
          </div>
          
          <button
            onClick={nextPage}
            className="p-2 rounded-full bg-white border border-gray-200 hover:bg-amber-50 hover:border-amber-300 transition-colors"
            aria-label="Next"
          >
            <ChevronRight className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        {/* Stats */}
        <div className="mt-12 text-center">
          <p className="text-gray-500 text-sm">
            <span className="font-bold text-amber-600">{testimonials.length}+</span> pelanggan berpuas hati dari seluruh Malaysia
          </p>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;